import styled from "@emotion/styled";
import { space } from "@smartsheet/design-tokens";
import { createFocusRingStyles } from "../../../../util/createFocusRingStyles";
import { RequiredThemeKeys } from "../../../../util/theme";
import { Clickable } from "../../../clickable";
import { FocusMode } from "../../../focusMode";
import { DAY_STYLED_CLASS_NAME } from "../components/DatePickerDay";
import {
    getDayBorderRadius,
    getDayBackgroundColorActive,
    getDayBackgroundColorHover,
    getDayBackgroundColorToday,
    getDayBackgroundColorTodayActive,
    getDayBackgroundColorTodayHover,
    getDayColor,
    getDayColorToday,
    getDayFocusRingGap,
    getDayBorderColorToday,
    getDayBackgroundColorSelected,
    getDayColorSelected,
    datePickerTheme,
    getDayColorDisabled,
    getDayBackgroundColorFocused,
    getDayFontWeightToday,
    getDayFontWeightSelected,
    getDisabledBumperStyles,
} from "./datePickerTheme";

export interface DatePickerDayButtonStyledProps {
    focusMode: FocusMode;
    isDisabled: boolean;
    isSelected: boolean;
    isToday: boolean;
    themeMode: RequiredThemeKeys;
    isInRange?: boolean;
}

const shouldForwardProp = (propName: string): boolean => {
    switch (propName) {
        // NOT blocking `isDisabled` so it gets forwarded on to the Clickable.
        case "focusMode":
        case "isSelected":
        case "isToday":
            // Just add any extra props here :)
            return false;
    }
    return true;
};

const getKeyboardStyles = (isSelected: boolean, _isToday: boolean, themeMode: RequiredThemeKeys) => `
    &:focus {
        background-color: ${getDayBackgroundColorFocused({ themeMode })};
        // Make sure a selected element shows above its peers
        // so the focus ring will not be occluded.
        z-index: 1;
        box-shadow: 0px 0px 0px 3px #ffffff;
    }

    ${
        isSelected
            ? `
                &,
                &:focus {
                    background-color: ${getDayBackgroundColorSelected({ themeMode })};

                    & .${DAY_STYLED_CLASS_NAME} {
                        color: ${getDayColorSelected({ themeMode })};
                        font-weight: ${getDayFontWeightSelected({ themeMode })};
                    }
                }
            `
            : ""
    }
`;

const getMouseStyles = (isSelected: boolean, isToday: boolean, themeMode: RequiredThemeKeys) => `

    &:hover {
        background-color: ${
            isToday ? getDayBackgroundColorTodayHover({ themeMode }) : getDayBackgroundColorHover({ themeMode })
        };
    }

    &:active {
        background-color: ${
            isToday ? getDayBackgroundColorTodayActive({ themeMode }) : getDayBackgroundColorActive({ themeMode })
        };
    }

    ${
        isSelected
            ? `
            &,
            &:hover,
            &:active {
                background-color: ${getDayBackgroundColorSelected({ themeMode })};

                & .${DAY_STYLED_CLASS_NAME} {
                    color: ${getDayColorSelected({ themeMode })};
                    font-weight: ${getDayFontWeightSelected({ themeMode })};
                }
            }
        `
            : ""
    }
`;

const getDisabledStyles = ({ isDisabled, themeMode }: DatePickerDayButtonStyledProps) =>
    isDisabled
        ? `
            cursor: not-allowed;

            &,
            &:focus,
            &:hover,
            &:active {
                background-color: transparent;

                & .${DAY_STYLED_CLASS_NAME} {
                    color: ${getDayColorDisabled({ themeMode })};
                    ${getDisabledBumperStyles({ themeMode })}
                }
            }
        `
        : "";

export const DatePickerDayButtonStyled = styled(Clickable, {
    shouldForwardProp,
})<DatePickerDayButtonStyledProps>`
    box-sizing: border-box;
    border: 2px solid transparent;
    border-radius: ${({ themeMode }) => datePickerTheme.dayBorderRadius(themeMode)};

    height: ${space.space60};
    width: ${({ themeMode }) => datePickerTheme.dayWidth(themeMode)};
    color: ${getDayColor};

    ${({ isInRange, isToday, themeMode }) =>
        isToday &&
        `
            ${!isInRange ? `background-color: ${getDayBackgroundColorToday({ themeMode })};` : ""};
            border-color: ${getDayBorderColorToday({ themeMode })};
            & .${DAY_STYLED_CLASS_NAME} {
                color: ${getDayColorToday({ themeMode })};
                font-weight: ${getDayFontWeightToday({ themeMode })};
            }
        `}
    // We need to treat a LOT of the styles differently if we are in keyboard or mouse mode.
    ${({ focusMode, isSelected, isToday, themeMode }) =>
        focusMode === "keyboard"
            ? getKeyboardStyles(isSelected, isToday, themeMode)
            : getMouseStyles(isSelected, isToday, themeMode)}
    ${({ themeMode }) =>
        createFocusRingStyles({
            theme: themeMode,
            gap: getDayFocusRingGap({ themeMode }),
            componentBorderWidth: 2,
            componentRadius: getDayBorderRadius({ themeMode }),
            secondaryWidth: 0,
        })}
    // Putting this last so it always wins.
    ${getDisabledStyles};
`;
